/**
 * useCallBackgroundService — Registers a PersistentBar background service
 * when a Huddle01 call is active, so the mini call widget persists across
 * app navigation (outside of /messenger).
 *
 * Mount this inside the messenger route tree (where HuddleProvider lives).
 * It watches `activeCall` from the store and registers/unregisters accordingly.
 */

import { useEffect, useRef } from "react";
import { useBackgroundService } from "@slumbr/app-sdk";
import { useMessengerStore } from "../stores/messenger-store";
import { CallPersistentWidget } from "./call-persistent-widget";

const SERVICE_ID = "messenger-active-call";
const APP_ID = "messenger";

export function useCallBackgroundService() {
  const activeCall = useMessengerStore((s) => s.activeCall);
  const { register, unregister } = useBackgroundService();
  const isRegistered = useRef(false);

  useEffect(() => {
    if (activeCall && !isRegistered.current) {
      // Register the background service so PersistentBar shows the mini widget
      register({
        id: SERVICE_ID,
        appId: APP_ID,
        persistentUI: CallPersistentWidget,
      });
      isRegistered.current = true;
    } else if (!activeCall && isRegistered.current) {
      // Call ended — remove the background service
      unregister(SERVICE_ID);
      isRegistered.current = false;
    }
  }, [activeCall, register, unregister]);

  // On unmount: if a call is active, keep the background service registered
  // so the PersistentBar widget persists across navigation.
  // Subscribe to the store to auto-unregister when the call ends while
  // this hook is unmounted (e.g. user ends call from the persistent widget).
  useEffect(() => {
    return () => {
      if (isRegistered.current) {
        // Set up a store subscription to clean up when activeCall becomes null
        const unsub = useMessengerStore.subscribe((state) => {
          if (!state.activeCall && isRegistered.current) {
            unregister(SERVICE_ID);
            isRegistered.current = false;
            unsub();
          }
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
